import { forwardRef, useState, useEffect, useCallback, type ComponentPropsWithoutRef, type ReactNode } from 'react'
import { useGlobalStore } from '@highlight/ipc/react'
import { setOverlayInteractive, Tooltip } from '@highlight/ui'
import { useSidepanel } from '@renderer/overlay/atoms'
import { trackSidepanelClosed } from '@renderer/overlay/utils/track-sidepanel'
import { useSetSidepanelHotkeyOpened, useSidepanelOpenedViaKeybind } from '../../../atoms'
import { Minus, Plus, X } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { useIsWindowFocused } from '@renderer/chat'
import useTriggerHotkey from '@renderer/hooks/useTriggerHotkey'
import { isMac } from '@shared/utils/platform'
import { hasTahoeWindowControls } from '@renderer/utils/macos-version'

type TrafficLightKind = 'close' | 'minimize' | 'zoom'

/** macOS 26's zoom glyph: a square split on the diagonal, where earlier releases drew a plus. */
function TahoeZoomGlyph({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 10 10" aria-hidden="true" className={className}>
      <path d="M1.5 1.5H7L1.5 7Z" fill="currentColor" />
      <path d="M8.5 8.5H3L8.5 3Z" fill="currentColor" />
    </svg>
  )
}

interface TrafficLightLook {
  groupClassName: string
  dotClassName: string
  /** Each dot's colours, as the variables `DOT_FILL` paints from. */
  colors: Record<TrafficLightKind, string>
  glyphs: Record<TrafficLightKind, ReactNode>
}

// The native controls of the macOS release the app is running on. macOS 26 (Tahoe) shades each dot
// top to bottom, rims it in a darker step of its hue and draws the glyph in that hue; colours
// sampled from macOS 26 and converted from Display P3. Earlier releases are flat.
const TRAFFIC_LIGHT_LOOK: TrafficLightLook = hasTahoeWindowControls
  ? {
      groupClassName: 'gap-[9px]',
      dotClassName: '',
      colors: {
        close: '[--dot-top:#fb6a63] [--dot-bottom:#f2807a] [--dot-rim:#d0251b]',
        minimize: '[--dot-top:#f5b532] [--dot-bottom:#facc40] [--dot-rim:#d48c00]',
        zoom: '[--dot-top:#62cd2d] [--dot-bottom:#91d572] [--dot-rim:#4fa52a]',
      },
      glyphs: {
        close: <X aria-hidden="true" strokeWidth={4} className="text-[#a30100]" />,
        minimize: <Minus aria-hidden="true" strokeWidth={4} className="text-[#c14401]" />,
        zoom: <TahoeZoomGlyph className="text-[#368800]" />,
      },
    }
  : {
      groupClassName: 'gap-2',
      dotClassName: 'border-black-3 text-black-11 dark:border-white-2 border',
      colors: {
        close: '[--dot-top:#ff5f57] [--dot-bottom:#ff5f57] [--dot-rim:transparent]',
        minimize: '[--dot-top:#febc2e] [--dot-bottom:#febc2e] [--dot-rim:transparent]',
        zoom: '[--dot-top:#28c840] [--dot-bottom:#28c840] [--dot-rim:transparent]',
      },
      glyphs: {
        close: <X aria-hidden="true" strokeWidth={4} />,
        minimize: <Minus aria-hidden="true" strokeWidth={4} />,
        zoom: <Plus aria-hidden="true" strokeWidth={4} />,
      },
    }

const DOT_FILL =
  'bg-gradient-to-b from-[color:var(--dot-top)] to-[color:var(--dot-bottom)] shadow-[inset_0_0_0_0.5px_var(--dot-rim)]'
/** `DOT_FILL` while the group is hovered: an unfocused window's grey dots colour up, as they do natively. */
const DOT_FILL_ON_HOVER =
  'group-hover/traffic:bg-gradient-to-b group-hover/traffic:from-[color:var(--dot-top)] group-hover/traffic:to-[color:var(--dot-bottom)] group-hover/traffic:shadow-[inset_0_0_0_0.5px_var(--dot-rim)]'

interface TrafficLightProps extends Omit<ComponentPropsWithoutRef<'button'>, 'children'> {
  kind: TrafficLightKind
  'aria-label': string
}

// macOS-style window control dot. 14px visual, expanded hit area; the glyph
// appears when the group is hovered (like native traffic lights). Forwards its ref and props
// so a tooltip trigger can render it.
const TrafficLight = forwardRef<HTMLButtonElement, TrafficLightProps>(function TrafficLight(
  { kind, disabled, className, ...props },
  ref,
) {
  const isWindowFocused = useIsWindowFocused()
  return (
    <button
      ref={ref}
      disabled={disabled}
      {...props}
      className={twMerge(
        "relative flex size-[14px] items-center justify-center rounded-full after:absolute after:-inset-1.5 after:content-['']",
        TRAFFIC_LIGHT_LOOK.dotClassName,
        TRAFFIC_LIGHT_LOOK.colors[kind],
        '[&>svg]:size-2.5 [&>svg]:opacity-0 [&>svg]:transition-opacity group-hover/traffic:[&>svg]:opacity-100',
        disabled
          ? 'bg-element-hover group-hover/traffic:bg-element-active cursor-default group-hover/traffic:[&>svg]:opacity-0'
          : isWindowFocused
            ? DOT_FILL
            : `bg-element-active ${DOT_FILL_ON_HOVER}`,
        className,
      )}
    >
      {TRAFFIC_LIGHT_LOOK.glyphs[kind]}
    </button>
  )
})

const WINDOWS_ICON_SIZE = 16
const WINDOWS_BUTTON_CLASS =
  'flex h-full w-10 min-h-8 items-center justify-center rounded-none hover:bg-element-hover hover:text-foreground'

interface WindowsTitleButtonProps extends ComponentPropsWithoutRef<'button'> {
  'aria-label': string
}

// Forwards its ref and props so a tooltip trigger can render it.
const WindowsTitleButton = forwardRef<HTMLButtonElement, WindowsTitleButtonProps>(function WindowsTitleButton(
  { disabled, className, children, ...props },
  ref,
) {
  const isWindowFocused = useIsWindowFocused()
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      {...props}
      className={twMerge(
        WINDOWS_BUTTON_CLASS,
        isWindowFocused ? 'text-foreground-secondary' : 'text-foreground-disabled',
        disabled && 'hover:text-foreground-secondary cursor-default opacity-40 hover:bg-transparent',
        className,
      )}
    >
      {children}
    </button>
  )
})

function useSidepanelWindowControls() {
  const [sidepanelState, setSidepanelState] = useSidepanel()
  const setHotkeyOpened = useSetSidepanelHotkeyOpened()
  const openedViaKeybind = useSidepanelOpenedViaKeybind()
  const triggerHotkey = useTriggerHotkey()
  const [openCount, setOpenCount] = useGlobalStore<number>('general', 'sidepanel.openCount', 0)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [tooltipDisabled, setTooltipDisabled] = useState(false)

  const NUMBER_OF_OPENS_THAT_SHOW_AUTO_TOOLTIP = 5

  // Show tooltip automatically for first opens (skip if opened via keybind)
  useEffect(() => {
    if (sidepanelState === 'open' && openCount < NUMBER_OF_OPENS_THAT_SHOW_AUTO_TOOLTIP && !openedViaKeybind) {
      setOpenCount(openCount + 1)
      setTooltipOpen(true)
      setTooltipDisabled(false)
      const hideTimeout = setTimeout(() => setTooltipOpen(false), 3000)
      return () => clearTimeout(hideTimeout)
    } else if (sidepanelState !== 'open') {
      setTooltipOpen(false)
    }
  }, [sidepanelState, openedViaKeybind])

  // The controls only render while the panel is open, so this always closes it, the way the
  // other close paths do.
  const handleHide = useCallback(() => {
    setTooltipDisabled(true)
    trackSidepanelClosed('trigger')
    setHotkeyOpened(false)
    setOverlayInteractive(false)
    setSidepanelState('hidden')
  }, [setSidepanelState, setHotkeyOpened])

  const showAutoTooltip = openCount < NUMBER_OF_OPENS_THAT_SHOW_AUTO_TOOLTIP && !openedViaKeybind

  return {
    sidepanelState,
    handleHide,
    triggerHotkey,
    showAutoTooltip,
    tooltipOpen,
    tooltipDisabled,
  }
}

function MacOsWindowControls({
  onHide,
  triggerHotkey,
  showAutoTooltip,
  tooltipOpen,
  tooltipDisabled,
}: {
  onHide: () => void
  triggerHotkey: string
  showAutoTooltip: boolean
  tooltipOpen: boolean
  tooltipDisabled: boolean
}) {
  return (
    <div
      className={twMerge('group/traffic flex items-center pl-1 pr-1.5', TRAFFIC_LIGHT_LOOK.groupClassName)}
      data-no-drag
    >
      {/* The first few opens show the hotkey on their own; after that it waits for a hover. The key
          remounts the tooltip when the mode flips, so it never switches between controlled and not. */}
      <Tooltip
        key={showAutoTooltip ? 'auto' : 'hover'}
        open={showAutoTooltip ? tooltipOpen : undefined}
        disabled={!showAutoTooltip && tooltipDisabled}
      >
        <Tooltip.Trigger render={<TrafficLight kind="close" onClick={onHide} aria-label="Hide sidepanel" />} />
        <Tooltip.Content side="bottom">
          <Tooltip.Hotkey hotkey={triggerHotkey} />
        </Tooltip.Content>
      </Tooltip>
      <TrafficLight kind="minimize" onClick={onHide} aria-label="Minimize sidepanel" />
      <TrafficLight kind="zoom" aria-label="Open in window" disabled />
    </div>
  )
}

function WindowsOsWindowControls({
  onHide,
  triggerHotkey,
  showAutoTooltip,
  tooltipOpen,
  tooltipDisabled,
}: {
  onHide: () => void
  triggerHotkey: string
  showAutoTooltip: boolean
  tooltipOpen: boolean
  tooltipDisabled: boolean
}) {
  return (
    <div className="-webkit-app-region-no-drag flex h-full items-center" data-no-drag>
      <Tooltip.Provider>
        <Tooltip>
          <Tooltip.Trigger
            render={
              <WindowsTitleButton onClick={onHide} aria-label="Minimize sidepanel">
                <svg
                  width={WINDOWS_ICON_SIZE}
                  height={WINDOWS_ICON_SIZE}
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path d="M10 7H2V5H10" fill="currentColor" />
                </svg>
              </WindowsTitleButton>
            }
          />
          <Tooltip.Content side="bottom">Minimize</Tooltip.Content>
        </Tooltip>

        <Tooltip>
          {/* A wrapper, not the button: a disabled button gets no pointer events, so the tooltip would never open. */}
          <Tooltip.Trigger render={<div className="relative flex h-full" />}>
            <WindowsTitleButton aria-label="Open in window" disabled>
              <svg
                width={WINDOWS_ICON_SIZE}
                height={WINDOWS_ICON_SIZE}
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path d="M2 2H10V10H2V2ZM3 4V9H9V4H3Z" fill="currentColor" />
              </svg>
            </WindowsTitleButton>
          </Tooltip.Trigger>
          <Tooltip.Content side="bottom">Open in window</Tooltip.Content>
        </Tooltip>

        <Tooltip
          key={showAutoTooltip ? 'auto' : 'hover'}
          open={showAutoTooltip ? tooltipOpen : undefined}
          disabled={!showAutoTooltip && tooltipDisabled}
        >
          <Tooltip.Trigger
            render={
              <WindowsTitleButton onClick={onHide} aria-label="Hide sidepanel">
                <svg
                  width={WINDOWS_ICON_SIZE}
                  height={WINDOWS_ICON_SIZE}
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M9.5 3.205L8.795 2.5L6 5.295L3.205 2.5L2.5 3.205L5.295 6L2.5 8.795L3.205 9.5L6 6.705L8.795 9.5L9.5 8.795L6.705 6L9.5 3.205Z"
                    fill="currentColor"
                  />
                </svg>
              </WindowsTitleButton>
            }
          />
          <Tooltip.Content side="bottom">
            {showAutoTooltip ? null : 'Hide'}
            <Tooltip.Hotkey hotkey={triggerHotkey} />
          </Tooltip.Content>
        </Tooltip>
      </Tooltip.Provider>
    </div>
  )
}

// Panel window controls in the top bar. Close/minimize hide the panel toward
// its anchored edge; maximize is the expand/detach slot (wired in a later
// ticket). Fake chrome — macOS traffic lights on Mac, Windows caption buttons
// elsewhere — matching the Assistant floaty titlebar.
export function SidepanelTrigger() {
  const { sidepanelState, handleHide, triggerHotkey, showAutoTooltip, tooltipOpen, tooltipDisabled } =
    useSidepanelWindowControls()

  // Only show when sidepanel is open
  if (sidepanelState !== 'open') return null

  if (isMac) {
    return (
      <MacOsWindowControls
        onHide={handleHide}
        triggerHotkey={triggerHotkey}
        showAutoTooltip={showAutoTooltip}
        tooltipOpen={tooltipOpen}
        tooltipDisabled={tooltipDisabled}
      />
    )
  }

  return (
    <WindowsOsWindowControls
      onHide={handleHide}
      triggerHotkey={triggerHotkey}
      showAutoTooltip={showAutoTooltip}
      tooltipOpen={tooltipOpen}
      tooltipDisabled={tooltipDisabled}
    />
  )
}
